import { type Position } from './chess-engine';

export interface OpeningMove {
  from: Position;
  to: Position;
  frequency: number; // How often this move is played in the position
  performance: number; // How well this move performs (0-1)
}

export interface OpeningPosition {
  fen: string;
  moves: OpeningMove[];
}

// Basic opening book with common openings
const openingBook: Record<string, OpeningMove[]> = {
  // Starting position
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1': [
    { from: [6, 4], to: [4, 4], frequency: 0.35, performance: 0.52 }, // e4
    { from: [6, 3], to: [4, 3], frequency: 0.30, performance: 0.53 }, // d4
    { from: [7, 6], to: [5, 5], frequency: 0.15, performance: 0.51 }, // Nf3
    { from: [6, 2], to: [4, 2], frequency: 0.08, performance: 0.48 }, // c4
    { from: [7, 1], to: [5, 2], frequency: 0.05, performance: 0.49 }  // Nc3
  ],
  
  // After 1.e4
  'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq e3 0 1': [
    { from: [1, 4], to: [3, 4], frequency: 0.45, performance: 0.48 }, // e5
    { from: [1, 2], to: [3, 2], frequency: 0.25, performance: 0.49 }, // c5
    { from: [1, 4], to: [2, 4], frequency: 0.12, performance: 0.47 }, // e6
    { from: [1, 3], to: [3, 3], frequency: 0.08, performance: 0.46 }  // d5
  ],

  // After 1.d4
  'rnbqkbnr/pppppppp/8/8/3P4/8/PPP1PPPP/RNBQKBNR b KQkq d3 0 1': [
    { from: [1, 3], to: [3, 3], frequency: 0.35, performance: 0.48 }, // d5
    { from: [0, 6], to: [2, 5], frequency: 0.30, performance: 0.49 }, // Nf6
    { from: [1, 5], to: [3, 5], frequency: 0.12, performance: 0.47 }, // f5
    { from: [1, 4], to: [2, 4], frequency: 0.10, performance: 0.48 }  // e6
  ],

  // Sicilian Defense after 1.e4 c5
  'rnbqkbnr/pp1ppppp/8/2p5/4P3/8/PPPP1PPP/RNBQKBNR w KQkq c6 0 2': [
    { from: [7, 6], to: [5, 5], frequency: 0.85, performance: 0.55 }, // Nf3
    { from: [7, 1], to: [5, 2], frequency: 0.08, performance: 0.52 }, // Nc3
    { from: [6, 5], to: [4, 5], frequency: 0.04, performance: 0.48 }  // f4
  ],

  // King's Pawn Game after 1.e4 e5
  'rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq e6 0 2': [
    { from: [7, 6], to: [5, 5], frequency: 0.75, performance: 0.54 }, // Nf3
    { from: [5, 5], to: [4, 2], frequency: 0.12, performance: 0.52 }, // Bc4
    { from: [6, 5], to: [4, 5], frequency: 0.05, performance: 0.48 }, // f4
    { from: [7, 1], to: [5, 2], frequency: 0.04, performance: 0.51 }  // Nc3
  ],

  // Queen's Gambit after 1.d4 d5
  'rnbqkbnr/ppp1pppp/8/3p4/3P4/8/PPP1PPPP/RNBQKBNR w KQkq d6 0 2': [
    { from: [6, 2], to: [4, 2], frequency: 0.65, performance: 0.53 }, // c4
    { from: [7, 6], to: [5, 5], frequency: 0.20, performance: 0.51 }, // Nf3
    { from: [6, 1], to: [5, 1], frequency: 0.08, performance: 0.49 }  // b3
  ]
};

export function getOpeningMove(fen: string, openingStrength: number): OpeningMove | null {
  const moves = openingBook[fen];
  if (!moves || Math.random() > openingStrength) {
    return null;
  }

  // Weight moves by frequency and performance
  const weightedMoves = moves.map(move => ({
    move,
    weight: move.frequency * (0.5 + move.performance * 0.5)
  }));

  const totalWeight = weightedMoves.reduce((sum, item) => sum + item.weight, 0);
  let random = Math.random() * totalWeight;

  for (const item of weightedMoves) {
    random -= item.weight;
    if (random <= 0) {
      return item.move;
    }
  }

  return moves[0]; // Fallback to first move
}

export function getFenFromGameState(gameState: any): string {
  // Simple FEN generation - in a real implementation, this would be more comprehensive
  // For now, we'll use a simplified version that matches our opening book keys
  let fen = '';
  
  // Board representation
  for (let row = 0; row < 8; row++) {
    let emptyCount = 0;
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        if (emptyCount > 0) {
          fen += emptyCount.toString();
          emptyCount = 0;
        }
        const pieceChar = piece.color === 'white' 
          ? piece.type.toUpperCase() 
          : piece.type.toLowerCase();
        fen += pieceChar;
      } else {
        emptyCount++;
      }
    }
    if (emptyCount > 0) {
      fen += emptyCount.toString();
    }
    if (row < 7) fen += '/';
  }
  
  // Side to move
  fen += ` ${gameState.sideToMove === 'white' ? 'w' : 'b'}`;
  
  // Castling availability (fix the boolean checks)
  let castling = '';
  // Note: GameState uses whiteKingSide/whiteQueenSide keys
  if (gameState.castlingRights?.whiteKingSide === true) castling += 'K';
  if (gameState.castlingRights?.whiteQueenSide === true) castling += 'Q';
  if (gameState.castlingRights?.blackKingSide === true) castling += 'k';
  if (gameState.castlingRights?.blackQueenSide === true) castling += 'q';
  fen += ` ${castling || '-'}`;
  
  // En passant target square
  const enPassant = gameState.enPassantTarget 
    ? String.fromCharCode(97 + gameState.enPassantTarget[1]) + (8 - gameState.enPassantTarget[0])
    : '-';
  fen += ` ${enPassant}`;
  
  // Halfmove and fullmove counters
  fen += ` ${gameState.halfmoveClock} ${gameState.fullmoveNumber}`;
  
  return fen;
}